/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useState, useEffect } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import './DiscardReasonsPopup.css'
import ButtonComponent from '@components/base/button/Button'
import AutoComplete from '@components/base/autoComplete/AutoComplete'
import { DISCARD_POPUP_CONFIG, MESSAGES, ROUTES } from '../../constants'
import { ManualReviewAPIEndpoints } from '../../api'
import { toastMessage, showLoading } from '@recoil/atoms/atoms'
import { fetchLookupDataSelector } from '@components/recoil/selectors/lookupSelectors'
import { fetchDataSelector } from '@components/recoil/selectors/selectors'
import { useTrackAnprLastActivity } from '@components/custom/anprLastActivity'
import utils from '@utils/utils'
import { useNavigate } from 'react-router-dom'

const DiscardReasonsPopup = ({
  onCancel,
  onConfirm,
  observationId,
  contractId,
  postDiscardRoute,
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState(null)
  const [discardReasons, setDiscardReasons] = useState([])
  const [loading, setLoading] = useState(false)
  const getCoreData = useRecoilValue(fetchDataSelector)
  const getCoreLookupData = useRecoilValue(fetchLookupDataSelector)
  const setToastMessage = useSetRecoilState(toastMessage)
  const setShowLoading = useSetRecoilState(showLoading)
  const navigate = useNavigate()
  const { trackLastActivity } = useTrackAnprLastActivity({ autoTrack: false })

  const getDiscardReasonsList = (response) => {
    const data = response?.data
    if (Array.isArray(data)) {
      return data
    }

    if (Array.isArray(data?.data)) {
      return data?.data
    }

    return []
  }

  useEffect(() => {
    fetchDiscardReasons()
  }, [])

  const fetchDiscardReasons = async () => {
    try {
      setLoading(true)
      setShowLoading(true)
      const response = await getCoreLookupData(
        ManualReviewAPIEndpoints?.getDiscardReasons?.method,
        ManualReviewAPIEndpoints?.getDiscardReasons?.url?.replace(
          '__contractId__',
          contractId
        )
      )

      const reasons = getDiscardReasonsList(response)
      setDiscardReasons(reasons)
      if (!reasons?.length) {
        setSelectedReasonId(null)
      }
    } catch (error) {
      setToastMessage({
        message: error?.message || MESSAGES?.discard?.error,
        type: utils?.toastStatus?.error,
      })
      setDiscardReasons([])
    } finally {
      setLoading(false)
      setShowLoading(false)
    }
  }

  const handleReasonChange = (event, params) => {
    setSelectedReasonId(params?.value || null)
  }

  const handleSave = async () => {
    trackLastActivity()

    const selectedReason = (
      Array.isArray(discardReasons) ? discardReasons : []
    ).find((reason) => reason.id === parseInt(selectedReasonId))

    const url = `${
      ManualReviewAPIEndpoints?.saveDiscard?.url
    }?observationID=${observationId}&discardID=${selectedReasonId}&discardName=${encodeURIComponent(
      selectedReason?.discardReason
    )}`

    try {
      setShowLoading(true)
      const response = await getCoreData(
        ManualReviewAPIEndpoints?.saveDiscard?.method,
        url,
        null,
        null,
        null,
        true,
        utils?.apiDomainName?.privateLand?.name
      )

      if (response?.status === utils?.apiResponseCode?.successCode) {
        setToastMessage({
          message: response?.data?.message || MESSAGES?.discard?.success,
          type: utils?.toastStatus?.success,
        })
        onCancel()
        navigate(postDiscardRoute ?? ROUTES?.MANUAL_REVIEW)
      } else {
        setToastMessage({
          message: response?.data?.message || MESSAGES?.discard?.failed,
          type: utils?.toastStatus?.error,
        })
      }
    } catch (error) {
      setToastMessage({
        message: error?.message || MESSAGES?.discard?.error,
        type: utils?.toastStatus?.error,
      })
    } finally {
      setShowLoading(false)
    }
  }

  return (
    <div
      id={DISCARD_POPUP_CONFIG?.dialogId}
      className={DISCARD_POPUP_CONFIG?.dialogClass}
      data-testid={DISCARD_POPUP_CONFIG?.dialogId}
    >
      <div className={DISCARD_POPUP_CONFIG?.containerClass}>
        <div className="discard-dialog-content">
          <span className="dialog-title">{DISCARD_POPUP_CONFIG?.title}</span>

          <div className="discard-reason-field">
            <AutoComplete
              id={DISCARD_POPUP_CONFIG?.autoCompleteField?.id}
              dataTestId={DISCARD_POPUP_CONFIG?.autoCompleteField?.dataTestId}
              field={{
                ...DISCARD_POPUP_CONFIG?.autoCompleteField,
                valueField: DISCARD_POPUP_CONFIG?.autoCompleteField?.valueField,
                textField: DISCARD_POPUP_CONFIG?.autoCompleteField?.textField,
                dataSource: discardReasons,
              }}
              value={selectedReasonId}
              onChangeHandler={handleReasonChange}
            />
          </div>
        </div>

        <div className="discard-dialog-footer">
          <ButtonComponent
            Field={{
              fieldValue: DISCARD_POPUP_CONFIG?.buttons?.cancel?.label,
              fieldLabel: DISCARD_POPUP_CONFIG?.buttons?.cancel?.label,
              variant: DISCARD_POPUP_CONFIG?.buttons?.cancel?.variant,
            }}
            dataTestId={DISCARD_POPUP_CONFIG?.buttons?.cancel?.dataTestId}
            className={DISCARD_POPUP_CONFIG?.buttons?.cancel?.className}
            onClickHandler={onCancel}
          />

          <ButtonComponent
            Field={{
              fieldValue: DISCARD_POPUP_CONFIG?.buttons?.save?.label,
              fieldLabel: DISCARD_POPUP_CONFIG?.buttons?.save?.label,
              variant: DISCARD_POPUP_CONFIG?.buttons?.save?.variant,
              disabled: loading || !selectedReasonId,
            }}
            dataTestId={DISCARD_POPUP_CONFIG?.buttons?.save?.dataTestId}
            className={DISCARD_POPUP_CONFIG?.buttons?.save?.className}
            onClickHandler={handleSave}
          />
        </div>
      </div>
    </div>
  )
}

export default DiscardReasonsPopup
